import { DEFAULT_SETTINGS } from '../data/billTemplates';
import { composeReceipt } from './composeReceipt';

/**
 * The time a bill was made, when the bill records one.
 *
 * Only bills created in this session carry `createdAt`; ones loaded back from
 * the API have a date and no clock. Reprinting an old bill with the current
 * time would be a plain lie, so the line is simply left off instead.
 */
/**
 * ESC/POS commands that make the *printer* draw a QR code.
 *
 * Deliberately not a rendered image: every thermal printer since about 2010
 * implements `GS ( k` in firmware, so this needs no QR library, no bitmap and
 * no bandwidth, and comes out at the printer's own resolution — which scans far
 * more reliably than a downscaled picture.
 *
 * Model 2, module size 6 (about 1cm on 80mm paper) and error correction level M
 * — enough redundancy to survive a smudged thermal print.
 */
const qrCommands = (data) => {
  const bytes = new TextEncoder().encode(String(data));
  const len = bytes.length + 3;
  const cmd = [
    GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,   // model 2
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x06,         // module size
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x31,         // error correction M
    GS, 0x28, 0x6B, len & 0xFF, (len >> 8) & 0xFF, 0x31, 0x50, 0x30,
    ...bytes,                                              // store the data
    GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30,         // print it
  ];
  return cmd;
};
// ESC/POS Commands
const ESC = 0x1B;
const GS = 0x1D;

/**
 * A CODE128 barcode, the one a shop counter scanner reads without setup.
 *
 * `{B` selects code set B, which covers the printable ASCII a bill number uses.
 * Anything outside that cannot be encoded at all, so it is dropped rather than
 * sent — a printer given a byte it cannot encode prints nothing and swallows
 * the rest of the receipt with it.
 */
const barcodeCommands = (data) => {
  const clean = String(data).replace(/[^\x20-\x7E]/g, '');
  if (!clean) return [];
  const bytes = new TextEncoder().encode(`{B${clean}`);
  return [
    GS, 0x68, 0x50,             // height: 80 dots, about 10mm
    GS, 0x77, 0x02,             // module width
    GS, 0x48, 0x02,             // print the characters below the bars
    GS, 0x6B, 0x49, bytes.length, ...bytes,
  ];
};

export const PrinterCommands = {
  INIT: [ESC, 0x40],
  TEXT_LEFT: [ESC, 0x61, 0x00],
  TEXT_CENTER: [ESC, 0x61, 0x01],
  TEXT_RIGHT: [ESC, 0x61, 0x02],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_SIZE: [GS, 0x21, 0x11],
  NORMAL_SIZE: [GS, 0x21, 0x00],
  FEED_AND_CUT: [GS, 0x56, 0x41, 0x03], // Feeds 3 lines then cuts
  LF: [0x0A],
};

/**
 * Encodes a string to a Uint8Array
 */
const encodeText = (text) => {
  const encoder = new TextEncoder();
  return encoder.encode(text);
};

/**
 * Helper to combine multiple Uint8Arrays into one
 */
const concatArrays = (arrays) => {
  let totalLength = 0;
  for (const arr of arrays) {
    totalLength += arr.length;
  }
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
};

/**
 * Build the ESC/POS bytes for one bill.
 *
 * The layout is not decided here — `composeReceipt` does that once for every
 * output, so the paper, the preview and the browser bill cannot disagree. This
 * function only turns blocks into printer commands.
 */
export const buildReceiptData = (org, bill, settings = DEFAULT_SETTINGS) => {
  const cfg = { ...DEFAULT_SETTINGS, ...(settings || {}) };
  const { blocks, width } = composeReceipt(org, bill, cfg);
  const parts = [];

  const addCmd = (cmd) => parts.push(new Uint8Array(cmd));
  const addLine = (text = '') => {
    if (text) parts.push(encodeText(text));
    addCmd(PrinterCommands.LF);
  };
  const ALIGN = {
    left: PrinterCommands.TEXT_LEFT,
    center: PrinterCommands.TEXT_CENTER,
    right: PrinterCommands.TEXT_RIGHT,
  };

  addCmd(PrinterCommands.INIT);

  blocks.forEach((b) => {
    if (b.kind === 'blank') { addLine(''); return; }
    if (b.kind === 'divider') {
      addCmd(PrinterCommands.TEXT_LEFT);
      addLine('-'.repeat(b.width || width));
      return;
    }
    if (b.kind === 'qr' || b.kind === 'barcode') {
      addCmd(PrinterCommands.TEXT_CENTER);
      addCmd(b.kind === 'qr' ? qrCommands(b.data) : barcodeCommands(b.data));
      addCmd(PrinterCommands.LF);
      if (b.caption) addLine(b.caption);
      return;
    }

    addCmd(ALIGN[b.align] || PrinterCommands.TEXT_LEFT);
    if (b.bold) addCmd(PrinterCommands.BOLD_ON);
    if (b.big) addCmd(PrinterCommands.DOUBLE_SIZE);
    addLine(b.text);
    if (b.big) addCmd(PrinterCommands.NORMAL_SIZE);
    if (b.bold) addCmd(PrinterCommands.BOLD_OFF);
  });

  addCmd(PrinterCommands.FEED_AND_CUT);

  const once = concatArrays(parts);
  const copies = Math.max(1, Math.min(5, Number(cfg.copies) || 1));
  if (copies === 1) return once;
  // Repeat the whole receipt rather than asking the caller to print twice, so a
  // shop needing a customer copy and a shop copy gets both from one print.
  return concatArrays(Array.from({ length: copies }, () => once));
};

let cachedUsbDevice = null;
let isConnectingUSB = false;

export const connectUSB = async () => {
  if (!navigator.usb) throw new Error('WebUSB is not supported.');
  if (cachedUsbDevice) return true;
  if (isConnectingUSB) throw new Error('IGNORE_LOCK');
  
  isConnectingUSB = true;
  try {
    const device = await navigator.usb.requestDevice({ filters: [] });
    cachedUsbDevice = device;
    return true;
  } catch (err) {
    console.error('USB Connect Error:', err);
    if (err.name === 'NotFoundError') {
      throw new Error('No printer was selected. Please click on your printer in the popup and click Connect.');
    }
    if (err.name === 'SecurityError' || err.message.includes('Access denied')) {
      throw new Error('Access Denied by Windows. You MUST use Zadig (zadig.akeo.ie) to replace the printer driver with WinUSB.');
    }
    throw err;
  } finally {
    isConnectingUSB = false;
  }
};

export const getUsbStatus = () => {
  return cachedUsbDevice !== null;
};

export const disconnectUSB = async () => {
  if (cachedUsbDevice && cachedUsbDevice.opened) {
    try {
      await cachedUsbDevice.close();
    } catch(e) {}
  }
  cachedUsbDevice = null;
};

/**
 * Prints data via WebUSB API
 */
export const printViaUSB = async (data) => {
  if (!navigator.usb) {
    throw new Error('WebUSB is not supported by your browser.');
  }

  let device = cachedUsbDevice;
  try {
    if (!device) {
      device = await navigator.usb.requestDevice({ filters: [] });
      cachedUsbDevice = device;
    }
    
    await device.open();
    if (device.configuration === null) {
      await device.selectConfiguration(1);
    }
    await device.claimInterface(0);

    // Find the correct OUT endpoint (bulk transfer)
    let endpointNumber = null;
    for (const endpoint of device.configuration.interfaces[0].alternate.endpoints) {
      if (endpoint.direction === 'out') {
        endpointNumber = endpoint.endpointNumber;
        break;
      }
    }

    if (endpointNumber === null) {
      throw new Error('Could not find a valid USB endpoint to send data.');
    }

    // Send the raw ESC/POS bytes in chunks just like Bluetooth
    const CHUNK_SIZE = 64; // Small chunk size to prevent buffer overflow on cheap thermal printers
    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      const chunk = data.slice(i, i + CHUNK_SIZE);
      await device.transferOut(endpointNumber, chunk);
    }
    
    return true;
  } catch (error) {
    console.error('USB Print Error:', error);
    if (error.name === 'NotFoundError' || error.name === 'SecurityError') {
      cachedUsbDevice = null;
    }
    if (error.name === 'NotFoundError') {
      throw new Error('No printer selected in the browser popup.');
    }
    if (error.name === 'SecurityError' || error.message.includes('Access denied')) {
      throw new Error('Windows is blocking access. Please use Zadig (zadig.akeo.ie) to install the WinUSB driver for your printer.');
    }
    throw error;
  } finally {
    if (device && device.opened) {
      try {
        await device.close();
      } catch (e) {
        console.error('Error closing USB device', e);
      }
    }
  }
};

let cachedBluetoothDevice = null;
let cachedBluetoothCharacteristic = null;

const getWritableCharacteristic = async (server) => {
  const services = await server.getPrimaryServices();
  for (const service of services) {
    const characteristics = await service.getCharacteristics();
    for (const char of characteristics) {
      if (char.properties.write || char.properties.writeWithoutResponse) {
        return char;
      }
    }
  }
  return null;
};

export const getPermittedBluetoothDevices = async () => {
  if (navigator.bluetooth && navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      return devices.length > 0;
    } catch (e) {
      console.warn('getDevices not supported or failed', e);
    }
  }
  return false;
};

let isConnectingBT = false;

/**
 * Who is holding the radio: 'user' for a Connect press or a print, 'background'
 * for the supervisor and the patient retry loop.
 *
 * Without this the two are indistinguishable, and the lock refused both alike.
 * Refusing the user's second click is right; refusing their first one because a
 * background retry happens to be mid-attempt is not.
 */
let btLockOwner = null;

/**
 * Wait for whatever is currently using the radio to let go of it.
 *
 * The lock is taken by the supervisor and by the patient retry loop, not only
 * by the user pressing Connect. A print that landed while a background attempt
 * was in flight therefore failed outright with IGNORE_LOCK — a signal meant for
 * a double-clicked button, shown to a cashier who had pressed Print once. A
 * background attempt lasts seconds at most, so printing waits for it instead;
 * and if it succeeds, there is nothing left for the print to reconnect.
 */
const waitForBluetoothIdle = async (timeoutMs = 12000) => {
  const deadline = Date.now() + timeoutMs;
  while (isConnectingBT && Date.now() < deadline) await sleep(150);
  return !isConnectingBT;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Connect a device's GATT server and return a writable characteristic.
 *
 * `gatt.connect()` resolving does NOT mean the link is usable: cheap thermal
 * printers routinely drop it in the moment before services are read, which
 * surfaces as "GATT Server is disconnected. Cannot retrieve services." Chrome
 * throws that from getPrimaryServices(), not from connect(), so the failure
 * looks like a code error when it is really a flaky radio link.
 *
 * So: verify the link is still up after connecting, and retry the whole
 * sequence a couple of times. A second attempt usually succeeds.
 */
/**
 * Wait until the browser actually hears this device advertising.
 *
 * A device handed back by getDevices() is only a saved permission — Chrome has
 * no idea whether it is nearby, and calling gatt.connect() on it blind fails
 * with "Bluetooth Device is no longer in range" even when the printer is on the
 * desk. watchAdvertisements() is the documented way to find out: listen, and
 * connect once an advertisement arrives.
 *
 * Resolves true if heard, false on timeout or where the API is unavailable —
 * the caller then tries connecting directly, which is what a freshly chosen
 * device supports.
 */
const waitForAdvertisement = (device, timeoutMs = 6000) => {
  if (typeof device.watchAdvertisements !== 'function') return Promise.resolve(false);
  return new Promise((resolve) => {
    const controller = new AbortController();
    let done = false;
    const finish = (heard) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      device.removeEventListener('advertisementreceived', onAdvert);
      try { controller.abort(); } catch { /* already stopped */ }
      resolve(heard);
    };
    const onAdvert = () => finish(true);
    const timer = setTimeout(() => finish(false), timeoutMs);
    device.addEventListener('advertisementreceived', onAdvert, { once: true });
    device.watchAdvertisements({ signal: controller.signal }).catch(() => finish(false));
  });
};

const connectAndGetCharacteristic = async (device, attempts = 4) => {
  let lastErr = null;
  for (let i = 0; i < attempts; i++) {
    try {
      // Start every attempt from a known-clean state. Reusing a half-dead
      // handle is what produces "GATT Server is disconnected": the printer has
      // gone, but `connected` can still read true for a moment, so connect() is
      // skipped and getPrimaryServices() then throws.
      if (i > 0 && device.gatt.connected) {
        try { device.gatt.disconnect(); } catch { /* already gone */ }
        await sleep(300);
      }
      if (!device.gatt.connected) {
        // Fast path: try connecting directly. Paired thermal printers connect in ~100-300ms.
        try {
          await device.gatt.connect();
        } catch (directErr) {
          // If direct connect failed, listen briefly for advertisements if supported before retrying
          if (typeof device.watchAdvertisements === 'function') {
            await waitForAdvertisement(device, 1500);
          }
          await device.gatt.connect();
        }
      }

      // Let the link settle, then confirm it is genuinely up before asking for
      // services. Cheap thermal printers commonly drop it in this window.
      await sleep(250);
      if (!device.gatt.connected) throw new Error('GATT dropped immediately after connect');

      const char = await getWritableCharacteristic(device.gatt);
      if (char) return char;
      lastErr = new Error('No writable characteristic on this device');
    } catch (e) {
      lastErr = e;
      try { device.gatt.disconnect(); } catch { /* already gone */ }
      // Back off a little further each time: 400ms, 800ms, 1200ms.
      if (i < attempts - 1) await sleep(400 * (i + 1));
    }
  }
  throw lastErr || new Error('Could not establish a Bluetooth connection');
};

/**
 * Turn a raw Web Bluetooth failure into something a shop owner can act on.
 * The browser's own wording ("(Re)connect first with `device.gatt.connect`")
 * reads like a developer error and tells the user nothing useful.
 */
export const describeBluetoothError = (err) => {
  const raw = String(err?.message || err || '');
  if (/GATT Server is disconnected|GATT operation failed|dropped immediately/i.test(raw)) {
    return 'The printer dropped the connection. Check it is switched on, close to this device, and not already connected to a phone or another computer, then try again.';
  }
  if (/User cancelled|chooser was cancelled/i.test(raw)) {
    return 'Pairing was cancelled.';
  }
  if (/writable characteristic/i.test(raw)) {
    return 'That device is not a supported printer. Choose your thermal printer from the list.';
  }
  if (/not supported|WebBluetooth/i.test(raw)) {
    return 'This browser does not support Bluetooth printing. Use Chrome or Edge on a computer.';
  }
  if (raw === 'IGNORE_LOCK') {
    return 'The printer is still connecting. Wait a moment and print again.';
  }
  return raw || 'Bluetooth connection failed.';
};

/** Name of the printer currently connected, or the last one paired. */
export const getBluetoothDeviceName = () =>
  cachedBluetoothDevice?.name || null;

/**
 * Whether this browser can reconnect to an already-paired printer without
 * showing the chooser again. Needs navigator.bluetooth.getDevices(), which
 * Chrome and Edge keep behind the "Web Bluetooth new permissions backend"
 * flag. With it off there is no API that can reconnect silently, so a page
 * refresh always loses the printer — no code can work around that.
 */
/**
 * Best-effort browser name and version, without a hard-coded list of browsers.
 *
 * Prefers navigator.userAgentData.brands, which browsers populate themselves —
 * so Brave, Opera, Vivaldi, Samsung Internet or something released next year all
 * name themselves correctly with no change here. Falls back to reading the last
 * meaningful Name/Version token out of the user-agent string, skipping the
 * boilerplate every browser carries for historical reasons.
 */
const detectBrowser = () => {
  const data = navigator.userAgentData;
  if (data?.brands?.length) {
    // Chromium pads this list with a deliberately meaningless entry.
    const real = data.brands.filter((b) => !/not.?a.?brand/i.test(b.brand));
    const pick = real[real.length - 1] || data.brands[0];
    if (pick) return `${pick.brand} ${pick.version}`;
  }

  const ua = navigator.userAgent || '';
  // Tokens present in almost every UA for compatibility, which name nothing.
  const boilerplate = /^(mozilla|applewebkit|khtml|gecko|safari|mobile safari|mobile|version|like)$/i;
  // Short codes browsers use for themselves. Anything not listed passes through
  // under its own name, so an unknown browser still identifies itself.
  const aliases = {
    Edg: 'Edge', EdgA: 'Edge', OPR: 'Opera', CriOS: 'Chrome',
    FxiOS: 'Firefox', SamsungBrowser: 'Samsung Internet',
  };
  const tokens = [...ua.matchAll(/([A-Za-z][A-Za-z .]*?)\/(\d+[\d.]*)/g)]
    .map((m) => ({ name: m[1].trim(), version: m[2].split('.')[0] }))
    .filter((t) => !boilerplate.test(t.name));
  if (!tokens.length) return 'Unknown browser';
  // A browser that names itself wins wherever it sits: Samsung Internet puts
  // its token before Chrome's, so position alone would misreport it. Otherwise
  // take the last token, which is conventionally the most specific.
  const named = tokens.find((t) => aliases[t.name]);
  const pick = named || tokens[tokens.length - 1];
  return `${aliases[pick.name] || pick.name} ${pick.version}`;
};

/**
 * Exactly which pieces of Web Bluetooth this browser exposes, for display on the
 * Printer page. "It doesn't work" is not diagnosable; "getDevices missing" is.
 *
 * Nothing in the app branches on the browser name — the working code only ever
 * asks whether a capability exists, so any browser that has it works, and any
 * that does not is told so. The name is a label for the reader, nothing more.
 */
export const getBluetoothCapabilities = () => ({
  browser: detectBrowser(),
  hasBluetooth: Boolean(navigator.bluetooth),
  hasGetDevices: Boolean(navigator.bluetooth && navigator.bluetooth.getDevices),
  hasWatchAdvertisements: typeof window !== 'undefined'
    && typeof window.BluetoothDevice !== 'undefined'
    && typeof window.BluetoothDevice.prototype.watchAdvertisements === 'function',
  secureContext: Boolean(window.isSecureContext),
});

export const isSilentReconnectSupported = () =>
  Boolean(navigator.bluetooth && navigator.bluetooth.getDevices);

let boundDisconnectDevice = null;
const onGattServerDisconnected = () => {
  console.log('Bluetooth disconnected.');
  cachedBluetoothCharacteristic = null;
  window.dispatchEvent(new CustomEvent('org-printer-bt-disconnected'));
  // Unexpected drop. If the user still wants the printer, start recovering
  // immediately rather than waiting for the next page load.
  if (wantsBluetoothConnected()) startBluetoothSupervisor();
};

const attachDisconnectListener = (device) => {
  if (boundDisconnectDevice && boundDisconnectDevice !== device) {
    try {
      boundDisconnectDevice.removeEventListener('gattserverdisconnected', onGattServerDisconnected);
    } catch {}
  }
  boundDisconnectDevice = device;
  device.removeEventListener('gattserverdisconnected', onGattServerDisconnected);
  device.addEventListener('gattserverdisconnected', onGattServerDisconnected);
};

/**
 * Reconnect an already-permitted printer without showing the chooser.
 * Assumes the caller holds isConnectingBT, so it can be reused by both the
 * automatic path and the manual Connect button.
 */
const trySilentReconnect = async () => {
  if (!navigator.bluetooth || !navigator.bluetooth.getDevices) return false;
  try {
    const devices = await navigator.bluetooth.getDevices();
    if (devices.length === 0) {
      lastSilentFailure = 'This browser does not remember any printer yet.';
      return false;
    }
    // Prioritize the previously remembered printer device
    const remembered = getRememberedPrinterName();
    const sorted = [...devices].sort((a, b) => {
      if (a.name && a.name === remembered) return -1;
      if (b.name && b.name === remembered) return 1;
      return 0;
    });

    for (const device of sorted) {
      try {
        // Two attempts per device: this can run unprompted on page load, so it
        // must not tie up the radio. A fresh pair retries harder.
        const char = await connectAndGetCharacteristic(device, 2);
        cachedBluetoothDevice = device;
        cachedBluetoothCharacteristic = char;
        attachDisconnectListener(device);
        setBluetoothIntent(true);
        rememberPrinterName(device.name);
        window.dispatchEvent(new CustomEvent('org-printer-bt-connected'));
        return true;
      } catch (e) {
        // Not every permitted device is the printer — some will never have a
        // writable characteristic. Try the next one quietly.
        lastSilentFailure = describeBluetoothError(e);
        console.warn('Failed to auto-connect to permitted device', e);
      }
    }
  } catch (err) {
    console.error('Silent reconnect failed', err);
  }
  return false;
};

// Why the most recent silent attempt failed, in plain language.
let lastSilentFailure = null;

// ── Connection intent ──────────────────────────────────────────────────────
// What the *user* wants, which is not the same as what the radio is currently
// doing. A refresh, a crash, interference or a printer going to sleep all break
// the link; none of them mean the user asked to stop printing. Only pressing
// Disconnect does. Persisted, because a refresh wipes everything else.
const BT_INTENT_KEY = 'mitra.printer.bt.intent';
// The paired printer's name, remembered separately from the browser's own
// device permission. Browsers without getDevices() cannot hand a saved device
// back to the page, but they will still honour a name filter — which turns a
// chooser listing every radio in the building into one showing this printer.
const BT_NAME_KEY = 'mitra.printer.bt.name';

export const getRememberedPrinterName = () => {
  try {
    return localStorage.getItem(BT_NAME_KEY) || null;
  } catch {
    return null;
  }
};

const rememberPrinterName = (name) => {
  if (!name) return;
  try {
    localStorage.setItem(BT_NAME_KEY, name);
  } catch { /* private mode — the chooser simply stays unfiltered */ }
};

export const wantsBluetoothConnected = () => {
  try {
    // Absent means the user has never pressed Disconnect, so assume they want
    // the printer. Requiring an explicit 'connected' meant anyone who had paired
    // before this key existed — everyone, on the upgrade — got no reconnect at
    // all until they connected once more, with nothing on screen saying why.
    // Only Disconnect writes 'disconnected'.
    return localStorage.getItem(BT_INTENT_KEY) !== 'disconnected';
  } catch {
    return true;
  }
};

const setBluetoothIntent = (wanted) => {
  try {
    localStorage.setItem(BT_INTENT_KEY, wanted ? 'connected' : 'disconnected');
  } catch { /* private mode — the supervisor simply will not resume after a reload */ }
};

// Bumped to abandon an in-flight patient retry — see cancelAutoReconnect().
let patientRetryToken = 0;

// A plain-language record of the last reconnect attempt, so the Printer page can
// show what happened. Debugging this from the browser console is not something a
// shop owner can be asked to do.
let lastReconnectReport = null;
export const getLastReconnectReport = () => lastReconnectReport;

/**
 * Reconnect a already-paired printer, optionally keeping at it for a while.
 *
 * Patience matters after a page refresh. The page dies instantly, but the
 * printer does not notice for several seconds: until it does, it believes it is
 * still in a connection and stops advertising, so an immediate attempt fails
 * with "Bluetooth Device is no longer in range". Give up at that point and the
 * user has to reconnect by hand, even though the printer becomes reachable a
 * moment later.
 *
 * `isConnectingBT` is held only for each individual attempt, never across the
 * waits, so pressing Connect during the window is not blocked by the lock.
 */
export const autoReconnectBluetooth = async ({ patienceMs = 0 } = {}) => {
  const token = ++patientRetryToken;
  const deadline = Date.now() + patienceMs;
  let attempt = 0;
  const at = () => new Date().toLocaleTimeString();

  if (!navigator.bluetooth || !navigator.bluetooth.getDevices) {
    lastReconnectReport = { ok: false, tries: 0, at: at(),
      reason: 'This browser cannot reconnect a paired printer on its own.' };
    return false;
  }

  for (;;) {
    // A newer call, or a manual connect, supersedes this one.
    if (token !== patientRetryToken) return false;

    if (!isConnectingBT) {
      isConnectingBT = true;
      btLockOwner = 'background';
      try {
        attempt += 1;
        if (await trySilentReconnect()) {
          lastReconnectReport = { ok: true, tries: attempt, at: at(), reason: 'Reconnected' };
          return true;
        }
      } finally {
        isConnectingBT = false;
        btLockOwner = null;
      }
    }

    if (Date.now() >= deadline) {
      lastReconnectReport = { ok: false, tries: attempt, at: at(),
        reason: lastSilentFailure || 'The printer did not respond.' };
      return false;
    }
    // 1s, 2s, 3s, then every 3s until the deadline. Slow enough not to hammer
    // the radio, quick enough that the printer is picked up soon after it
    // starts advertising again.
    await sleep(Math.min(3000, 1000 * attempt));
  }
};

/**
 * Abandon any patient retry in progress. Called before a manual connect so the
 * background loop cannot grab the radio midway through the user's own attempt.
 */
export const cancelAutoReconnect = () => {
  patientRetryToken += 1;
};

// ── Supervisor ─────────────────────────────────────────────────────────────
// Keeps the printer connected for as long as the user wants it connected.
//
// The fixed 20-second window this replaces gave up while the printer was still
// switched off, or still out of range, and then nothing tried again until the
// user happened to reload. Losing the link is normal and constant; giving up on
// it is what made the app feel broken.
let supervisorToken = 0;

export const startBluetoothSupervisor = () => {
  const token = ++supervisorToken;

  (async () => {
    let wait = 1000;
    while (token === supervisorToken) {
      // The user pressed Disconnect: stand down until they say otherwise.
      if (!wantsBluetoothConnected()) return;

      // Nothing paired with this browser, or no way to look: there is nothing
      // to reconnect to, so stop rather than loop forever. Pairing starts the
      // supervisor again.
      if (!navigator.bluetooth || !navigator.bluetooth.getDevices) return;
      if (!getBluetoothStatus()) {
        try {
          const known = await navigator.bluetooth.getDevices();
          if (!known.length) {
            // Browser device permissions may hydrate moments after document load.
            await sleep(2000);
            if (token !== supervisorToken) return;
            const retryKnown = await navigator.bluetooth.getDevices();
            if (!retryKnown.length) return;
          }
        } catch {
          return;
        }
      }

      if (getBluetoothStatus()) {
        // Healthy. Poll gently so an unexpected drop is noticed within seconds
        // rather than waiting for a page load.
        wait = 1000;
        await sleep(3000);
        continue;
      }

      if (!isConnectingBT) {
        isConnectingBT = true;
        btLockOwner = 'background';
        try {
          if (await trySilentReconnect()) {
            lastReconnectReport = { ok: true, tries: 1, at: new Date().toLocaleTimeString(), reason: 'Reconnected' };
          }
        } finally {
          isConnectingBT = false;
          btLockOwner = null;
        }
      }
      if (token !== supervisorToken) return;

      if (getBluetoothStatus()) {
        wait = 1000;
        continue;
      }

      lastReconnectReport = {
        ok: false,
        tries: 1,
        at: new Date().toLocaleTimeString(),
        reason: lastSilentFailure || 'The printer did not respond. Still trying.',
      };

      await sleep(wait);
      // Back off towards 15s so a printer that is switched off overnight does
      // not hammer the radio, while a brief drop still recovers quickly.
      wait = Math.min(15000, Math.round(wait * 1.6));
    }
  })();
};

export const stopBluetoothSupervisor = () => {
  supervisorToken += 1;
};

export const connectBluetooth = async () => {
  if (!navigator.bluetooth) throw new Error('WebBluetooth is not supported.');
  // Stand down any background retry first: the user pressing Connect takes
  // priority, and two attempts on the radio at once fail each other.
  cancelAutoReconnect();
  if (isConnectingBT) {
    // Only a second press while the user's own attempt is running is a
    // double-click. Waiting out a background retry — seconds at most — is what
    // someone pressing Connect once expects, not an error.
    if (btLockOwner === 'user') throw new Error('IGNORE_LOCK');
    await waitForBluetoothIdle();
    // The attempt just waited on may well have connected the printer.
    if (getBluetoothStatus() && cachedBluetoothCharacteristic) return true;
    if (isConnectingBT) throw new Error('IGNORE_LOCK');
  }

  isConnectingBT = true;
  btLockOwner = 'user';
  try {
    if (cachedBluetoothDevice) {
      if (!cachedBluetoothDevice.gatt.connected || !cachedBluetoothCharacteristic) {
        try {
          cachedBluetoothCharacteristic = await connectAndGetCharacteristic(cachedBluetoothDevice);
        } catch (e) {
          // Drop the dead reference. Keeping it meant every later attempt
          // retried the same unreachable device instead of letting the user
          // pick the printer again.
          cachedBluetoothDevice = null;
          cachedBluetoothCharacteristic = null;
          throw e;
        }
      }
      setBluetoothIntent(true);
      startBluetoothSupervisor();
      window.dispatchEvent(new CustomEvent('org-printer-bt-connected'));
      // The lock is released by the finally below, not here: dropping it early
      // let the supervisor take it and then have it cleared out from under it.
      return true;
    }

    // Try to reconnect a printer already paired with this browser before
    // opening the chooser. Where the browser supports it, pressing Connect
    // after a refresh now just works, with no device list to pick from.
    if (await trySilentReconnect()) {
      return true;
    }

    const optionalServices = ['49535343-fe7d-4ae5-8fa9-9fafd205e455', '000018f0-0000-1000-8000-00805f9b34fb'];
    const remembered = getRememberedPrinterName();

    // Where the browser cannot return a saved device — Edge has no getDevices()
    // — the chooser is unavoidable. It does not have to be a wall of unnamed
    // radios, though: filtering by the remembered name shows just this printer,
    // so reconnecting is one click rather than hunting through the room.
    let device = null;
    if (remembered) {
      try {
        device = await navigator.bluetooth.requestDevice({
          filters: [{ name: remembered }],
          optionalServices,
        });
      } catch (e) {
        // Cancelling is a decision, not a failure to work around.
        if (/cancel/i.test(String(e?.message || e))) throw e;
        // Printer renamed, replaced, or out of range: fall through to the full
        // list rather than leaving the user stuck with an empty chooser.
        device = null;
      }
    }

    if (!device) {
      device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices,
      });
    }
    
    const char = await connectAndGetCharacteristic(device);

    cachedBluetoothDevice = device;
    cachedBluetoothCharacteristic = char;

    attachDisconnectListener(device);
    // The user chose this printer, so they want it connected until they say
    // otherwise. Survives refreshes, crashes and dropped links.
    setBluetoothIntent(true);
    rememberPrinterName(device.name);
    startBluetoothSupervisor();
    window.dispatchEvent(new CustomEvent('org-printer-bt-connected'));
    return true;
  } catch (err) {
    console.error('Bluetooth Connect Error:', err);
    throw err;
  } finally {
    isConnectingBT = false;
    btLockOwner = null;
  }
};

/**
 * The only thing that ends the connection on purpose.
 *
 * Clears the stored intent and stands the supervisor down, so nothing
 * reconnects behind the user's back. Every other way the link can end — a
 * refresh, a crash, interference, the printer sleeping — leaves the intent
 * alone and is treated as something to recover from.
 */
export const disconnectBluetooth = () => {
  setBluetoothIntent(false);
  stopBluetoothSupervisor();
  cancelAutoReconnect();
  if (cachedBluetoothDevice && cachedBluetoothDevice.gatt.connected) {
    cachedBluetoothDevice.gatt.disconnect();
  }
};

/**
 * Completely forgets the paired printer, clearing stored preferences
 * and revoking browser permissions via device.forget() where supported.
 */
export const forgetBluetoothPrinter = async () => {
  disconnectBluetooth();
  try {
    localStorage.removeItem(BT_NAME_KEY);
    localStorage.removeItem(BT_INTENT_KEY);
  } catch {}
  if (navigator.bluetooth && navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      for (const dev of devices) {
        if (typeof dev.forget === 'function') {
          await dev.forget();
        }
      }
    } catch (e) {
      console.warn('Could not revoke device permissions', e);
    }
  }
  cachedBluetoothDevice = null;
  cachedBluetoothCharacteristic = null;
  window.dispatchEvent(new CustomEvent('org-printer-bt-disconnected'));
};

export const getBluetoothStatus = () => {
  return cachedBluetoothDevice ? cachedBluetoothDevice.gatt.connected : false;
};

export const printViaBluetooth = async (data) => {
  if (!navigator.bluetooth) {
    throw new Error('WebBluetooth is not supported by your browser.');
  }

  try {
    // The supervisor may be mid-reconnect. Let it finish rather than fighting
    // it for the radio — both attempts lose when they overlap.
    await waitForBluetoothIdle();

    if (!cachedBluetoothDevice || !cachedBluetoothCharacteristic) {
      const reconnected = await autoReconnectBluetooth();
      if (!reconnected) {
        await connectBluetooth();
      }
    } else if (!cachedBluetoothDevice.gatt.connected) {
      await cachedBluetoothDevice.gatt.connect();
      cachedBluetoothCharacteristic = await getWritableCharacteristic(cachedBluetoothDevice.gatt);
    }
    
    return await sendBluetoothData(cachedBluetoothCharacteristic, data);
  } catch (error) {
    console.error('Bluetooth Print Error:', error);
    // Clear cache if it's a fatal error that requires re-pairing
    if (error.name === 'NotFoundError' || error.name === 'SecurityError' || error.name === 'NetworkError') {
      cachedBluetoothDevice = null;
      cachedBluetoothCharacteristic = null;
    }
    throw error;
  }
};

const sendBluetoothData = async (characteristic, data) => {
  // Increased chunk size to 128 bytes (most modern ESC/POS BLE printers support 128-256)
  const CHUNK_SIZE = 128; 
  for (let i = 0; i < data.length; i += CHUNK_SIZE) {
    const chunk = data.slice(i, i + CHUNK_SIZE);
    if (characteristic.properties.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValue(chunk);
    }
    // Removed the artificial 10ms delay to drastically speed up printing
  }
  return true;
};

export const printViaWifi = async (data, ip) => {
  if (!ip) throw new Error('Please enter the Wi-Fi printer IP address.');
  
  try {
    const { fetchWithAuth } = await import('./api');
    
    const response = await fetchWithAuth(`/print/wifi?ip=${ip}`, {
      method: 'POST',
      body: data,
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || 'Wi-Fi print failed. Check if printer IP is correct and on same network.');
    }
    return true;
  } catch (err) {
    throw new Error(err.message || 'Wi-Fi print failed. Make sure the printer is on the same network.');
  }
};
